import { Stack } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useMedicationRequestByOrganization } from "@/services/fhir/medicationrequest/hooks/useMedicationRequestByOrganization";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  buildMedicationInsightCharts,
  buildMedicationPatientRows,
  MEDICATION_STATUS_TRANSLATION,
} from "src-next/modules/clinical-insights/transformers/mapMedicationInsights";
import { useFhirInsightLoadPhase } from "src-next/modules/clinical-insights/hooks/useFhirInsightLoadPhase";
import { matchesPractitionerFilter } from "src-next/modules/clinical-insights/helpers/filterResourcesByPractitioner";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";

const texts = getInsightAnalysisTexts("tratamentos");

export function PrescriptionAnalysis({
  count,
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
}: ClinicalInsightChildProps & { count?: number }) {
  const { organizationId } = useOrganizationContext();
  const practitionerFilter = practitionerId || "all";

  const {
    data: medData,
    loading: medLoading,
    fetchMedicationRequests,
  } = useMedicationRequestByOrganization({
    organizationId: organizationId || "",
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    includePatient: true,
    count: count || 250,
    includeEncounter: false,
  });

  useEffect(() => {
    if (organizationId && isActive) fetchMedicationRequests();
  }, [organizationId, dateRange?.startDate, dateRange?.endDate, isActive, fetchMedicationRequests]);

  const { hasData } = useFhirInsightLoadPhase(
    Boolean(organizationId) && isActive,
    medLoading,
    onLoadPhaseChange
  );

  const medications = useMemo(() => {
    const list = medData?.medicationRequests || [];
    if (practitionerFilter === "all") return list;
    return list.filter((med) =>
      matchesPractitionerFilter(practitionerFilter, med.requester?.reference)
    );
  }, [medData?.medicationRequests, practitionerFilter]);

  const patients = medData?.patients || [];

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;
  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel
  );

  const { statusItems, rankingItems, total } = useMemo(
    () => buildMedicationInsightCharts(medications),
    [medications]
  );

  const patientRows = useMemo(
    () => buildMedicationPatientRows(medications, patients),
    [medications, patients]
  );

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const percentage = total > 0 ? Math.round((sorted[0].value / total) * 100) : 0;
    return `${percentage}% das ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, total]);

  const loadPhase = medLoading ? "records" : "done";

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#4caf50"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={loadPhase}
        patientsProgress={{ loaded: 0, total: 0 }}
        hasData={hasData || medications.length > 0}
        isEmpty={total === 0 && !medLoading}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={() => fetchMedicationRequests()}
        statusChart={{ insightText: statusInsightTitle, items: statusItems }}
        rankingChart={{ insightText: texts.rankingHint, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={MEDICATION_STATUS_TRANSLATION}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
